import React, { useEffect, useState } from "react";

// Module-level cache for the site-wide schema bundle, keyed by language.
const _schemaStackCache = {};
const _schemaStackPromise = {};

async function fetchSchemaStack(api, lang = "en") {
    lang = (lang || "en").split("-")[0];
    if (_schemaStackCache[lang]) return _schemaStackCache[lang];
    if (_schemaStackPromise[lang]) return _schemaStackPromise[lang];
    const url = `${api}/api/seo/schema-stack?lang=${lang}`;
    _schemaStackPromise[lang] = fetch(url)
        .then((r) => (r.ok ? r.json() : []))
        .then((json) => {
            _schemaStackCache[lang] = Array.isArray(json) ? json : [];
            return _schemaStackCache[lang];
        })
        .catch(() => {
            _schemaStackCache[lang] = [];
            return _schemaStackCache[lang];
        });
    return _schemaStackPromise[lang];
}

// Module-level cache for translated SEO meta (source -> {lang: translated}).
const _metaTrCache = new Map();

async function translateMeta(api, texts, lang) {
    const want = texts.filter(Boolean);
    const missing = want.filter((t) => !_metaTrCache.get(`${lang}|${t}`));
    if (missing.length) {
        try {
            const r = await fetch(`${api}/api/i18n/translate`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ texts: missing, target_lang: lang }),
            });
            const json = r.ok ? await r.json() : { translations: {} };
            const map = json.translations || {};
            missing.forEach((t) => _metaTrCache.set(`${lang}|${t}`, map[t] || t));
        } catch {
            missing.forEach((t) => _metaTrCache.set(`${lang}|${t}`, t));
        }
    }
    const out = {};
    want.forEach((t) => { out[t] = _metaTrCache.get(`${lang}|${t}`) || t; });
    return out;
}


/**
 * SEOHead — declarative per-page meta + JSON-LD injection (no react-helmet needed).
 *
 * Props:
 *   title          page title (≤60 chars recommended)
 *   description    meta description (≤160 chars)
 *   keywords       array of strings
 *   canonical      path or full URL (omit to auto-use current href)
 *   image          OG image (path or full URL)
 *   type           "website" | "article" | "product"
 *   jsonLd         single JSON-LD object OR array of them — stacked on top of site bundle
 *   breadcrumbs    array of { name, path } — auto-converted to BreadcrumbList schema
 *   publishedAt    ISO date for article:published_time
 *   author         author name
 *   noindex        true → emit <meta name="robots" content="noindex,nofollow">
 *   includeSiteSchemas  default true → fetches /api/seo/schema-stack and stacks Organization+WebSite+SoftwareApp+FAQ
 */
export default function SEOHead({
    title,
    description,
    keywords,
    canonical,
    image,
    type = "website",
    jsonLd = null,
    breadcrumbs = null,
    publishedAt,
    author,
    noindex = false,
    includeSiteSchemas = true,
    alternates = null,
    htmlLang = null,
    preLocalized = false,
}) {
    const [siteSchemas, setSiteSchemas] = useState([]);
    const [metaTr, setMetaTr] = useState(null); // {source: translated} when ES
    const [langTick, setLangTick] = useState(0);
    const API = process.env.REACT_APP_BACKEND_URL;

    // Re-run meta/schema localization when the language changes at runtime
    // (e.g. the auto-detector switching to ES without a full reload).
    useEffect(() => {
        const onLang = () => setLangTick((t) => t + 1);
        window.addEventListener("languageChanged", onLang);
        return () => window.removeEventListener("languageChanged", onLang);
    }, []);

    useEffect(() => {
        if (!includeSiteSchemas) return;
        let cancelled = false;
        const lang = (document.documentElement.lang || "en").split("-")[0];
        fetchSchemaStack(API, lang).then((s) => { if (!cancelled) setSiteSchemas(s); });
        const onLang = () => {
            const l = (document.documentElement.lang || "en").split("-")[0];
            fetchSchemaStack(API, l).then((s) => { if (!cancelled) setSiteSchemas(s); });
        };
        window.addEventListener("languageChanged", onLang);
        return () => { cancelled = true; window.removeEventListener("languageChanged", onLang); };
    }, [API, includeSiteSchemas]);

    // Translate title/description/keywords for non-English locales so the browser
    // tab, search snippets and social previews are in the donor's language too.
    // For pages whose copy is already authored in the target language (e.g. the
    // dedicated Spanish landing pages), force <html lang> so schema + bots see ES,
    // and skip runtime meta translation (title/description are already localized).
    useEffect(() => {
        if (!htmlLang) return;
        const prev = document.documentElement.lang;
        document.documentElement.lang = htmlLang;
        try { window.dispatchEvent(new Event("languageChanged")); } catch { /* noop */ }
        return () => {
            document.documentElement.lang = prev;
            try { window.dispatchEvent(new Event("languageChanged")); } catch { /* noop */ }
        };
    }, [htmlLang]);

    useEffect(() => {
        const lang = (document.documentElement.lang || "en").split("-")[0];
        if (lang === "en" || preLocalized) { setMetaTr(null); return; }
        let cancelled = false;
        const texts = [title, description, ...(keywords || [])].filter(Boolean);
        if (!texts.length) { setMetaTr(null); return; }
        translateMeta(API, texts, lang).then((m) => { if (!cancelled) setMetaTr(m); });
        return () => { cancelled = true; };
    }, [API, title, description, keywords, langTick, preLocalized]);

    useEffect(() => {
        const base = window.location.origin;
        const cUrl = canonical ? (canonical.startsWith("http") ? canonical : base + canonical) : window.location.href;
        const ogImage = image ? (image.startsWith("http") ? image : base + image) : `${base}/api/marketing/file/01-storefront-hero-desktop.jpg`;

        const created = [];
        const setMeta = (attr, key, content) => {
            if (!content) return;
            let m = document.querySelector(`meta[${attr}="${key}"]`);
            if (!m) { m = document.createElement("meta"); m.setAttribute(attr, key); document.head.appendChild(m); created.push(m); }
            m.setAttribute("content", content);
        };

        const T = (s) => (metaTr && s && metaTr[s]) || s;
        const tTitle = T(title);
        const tDescription = T(description);
        const tKeywords = keywords?.length ? keywords.map(T) : keywords;

        const prevTitle = document.title;
        if (tTitle) document.title = tTitle;

        setMeta("name", "description", tDescription);
        if (tKeywords?.length) setMeta("name", "keywords", tKeywords.join(", "));
        if (author) setMeta("name", "author", author);
        if (noindex) setMeta("name", "robots", "noindex,nofollow");
        else setMeta("name", "robots", "index,follow,max-image-preview:large,max-snippet:-1");

        // OpenGraph
        setMeta("property", "og:type", type);
        setMeta("property", "og:title", tTitle);
        setMeta("property", "og:description", tDescription);
        setMeta("property", "og:url", cUrl);
        setMeta("property", "og:image", ogImage);
        setMeta("property", "og:image:width", "1200");
        setMeta("property", "og:image:height", "630");
        setMeta("property", "og:site_name", "CharityFundraiser");
        if (publishedAt) setMeta("property", "article:published_time", publishedAt);

        // Twitter
        setMeta("name", "twitter:card", "summary_large_image");
        setMeta("name", "twitter:site", "@charityfundraiser");
        setMeta("name", "twitter:title", tTitle);
        setMeta("name", "twitter:description", tDescription);
        setMeta("name", "twitter:image", ogImage);

        // Canonical
        let link = document.querySelector('link[rel="canonical"]');
        if (!link) { link = document.createElement("link"); link.setAttribute("rel", "canonical"); document.head.appendChild(link); created.push(link); }
        link.setAttribute("href", cUrl);

        // hreflang alternates (full bilingual SEO so Google/LLMs surface the right locale)
        const curLang = (document.documentElement.lang || "en").split("-")[0];
        const withLang = (lng) => {
            try { const u = new URL(cUrl); u.searchParams.set("lang", lng); return u.href; }
            catch { return cUrl; }
        };
        const xDefault = (() => { try { const u = new URL(cUrl); u.searchParams.delete("lang"); return u.href; } catch { return cUrl; } })();
        const altList = (alternates && alternates.length) ? alternates : [
            ["en", withLang("en")],
            ["es", withLang("es")],
            ["x-default", xDefault],
        ];
        altList.forEach(([hl, href]) => {
            const a = document.createElement("link");
            a.setAttribute("rel", "alternate");
            a.setAttribute("hreflang", hl);
            a.setAttribute("href", href);
            document.head.appendChild(a); created.push(a);
        });
        // og:locale reflects the page's current language
        setMeta("property", "og:locale", curLang === "es" ? "es_US" : "en_US");
        setMeta("property", "og:locale:alternate", curLang === "es" ? "en_US" : "es_US");

        // Build the stacked JSON-LD payload
        const allSchemas = [];
        if (includeSiteSchemas) allSchemas.push(...siteSchemas);
        if (jsonLd) {
            if (Array.isArray(jsonLd)) allSchemas.push(...jsonLd);
            else allSchemas.push(jsonLd);
        }
        if (breadcrumbs?.length) {
            allSchemas.push({
                "@context": "https://schema.org",
                "@type": "BreadcrumbList",
                "itemListElement": breadcrumbs.map((b, i) => ({
                    "@type": "ListItem",
                    "position": i + 1,
                    "name": b.name,
                    "item": b.path?.startsWith("http") ? b.path : base + (b.path || "/"),
                })),
            });
        }

        let script = null;
        if (allSchemas.length) {
            script = document.createElement("script");
            script.type = "application/ld+json";
            script.textContent = JSON.stringify(allSchemas);
            document.head.appendChild(script);
        }

        return () => {
            document.title = prevTitle;
            created.forEach(n => n.remove());
            if (script) script.remove();
        };
    }, [title, description, keywords, canonical, image, type, jsonLd, breadcrumbs, publishedAt, author, noindex, includeSiteSchemas, siteSchemas, API, metaTr, langTick, alternates]);

    return null;
}
